import React, { PropsWithChildren, useMemo } from 'react';
import { gql } from '@yi/core';
import { i18n_recommended_articles } from '@yi/i18n/phrases/i18n_recommended_articles';

import { navigate, logAlgoliaProductClickedEvent, algolia } from '~/common';
import { ARTICLES_PATHNAME } from '~/src/common/routes';
import { Omit } from '~/types';

import { ArticleCarousel } from './ArticleCarousel';
import { BaseCarouselComposerProps } from './BaseCarousel';
import { CarouselPlaceholder } from './CarouselPlaceholder';
import { WithPlaceholder } from './types';
import { ArticleCard } from '../cards';
import { useAlgoliaSearchClient } from '../hooks';
import { useLocale } from '../LocaleProvider';

type CarouselItem = gql.typesClient.Fragment_ArticleCard;
type Props = Omit<BaseCarouselComposerProps<CarouselItem>, 'items'>;

const { getLocalizedIndex, getNewestReplica, getNewerItemsFilter } = algolia;

const Placeholder: React.FC<Props> = ({ button }) => (
  <CarouselPlaceholder
    headerDimensions={{ headerHeight: 15, headerWidth: 230 }}
    cardDimension={ArticleCard.dimension}
    noButton={button === null}
    data-test-percy-hidden
  />
);
export const RecommendedArticlesCarousel: React.FC<PropsWithChildren<Props>> &
  WithPlaceholder<Props> = props => {
  const { locale } = useLocale();

  const indexName = getNewestReplica(getLocalizedIndex('articles', locale));
  const indexes = useMemo(
    () => [
      {
        indexName,
        params: {
          filters: getNewerItemsFilter(),
        },
        facetFilters: null,
      },
    ],
    [indexName],
  );

  const { hits, loading } = useAlgoliaSearchClient<CarouselItem>({
    indexes,
    locale,
  });
  if (loading && !hits.length) {
    return <Placeholder />;
  }
  const carouselItems = hits?.[0] ? hits[0].items : [];
  return carouselItems.length ? (
    <ArticleCarousel
      heading={i18n_recommended_articles(locale)}
      onCarouselItemClick={item =>
        logAlgoliaProductClickedEvent({
          contentID: item.id,
          indexName,
        })
      }
      onHeaderClick={() => navigate(ARTICLES_PATHNAME)}
      items={carouselItems}
      {...props}
    />
  ) : null;
};

RecommendedArticlesCarousel.Placeholder = Placeholder;
